export const RARITIES = ['UR', 'SSR', 'SR', 'R'];
export const RESOURCE_GROUPS = ['UR', 'SSR_LIMITED', 'SSR', 'SR', 'R'];
export const VALUE_KINDS = ['flat', 'percent'];
export const WHEEL_CATEGORIES = ['Creation', 'Material', 'Execution', 'Destiny'];
export const PERCENT_MODES = ['relative', 'points'];
export const DEFAULT_WEIGHT_FOCUS_EXPONENT = 2;
export const DEFAULT_SCORE_PRECISION_DIGITS = 5;
export const MAX_SCORE_PRECISION_DIGITS = 12;
export const DEFAULT_SOLVER_THREADS = 1;
export const SSR_CHOICE_ACQUISITION_PART_KEY = 'ssr_choice_acquisition_part';
export const SSR_ORDINARY_ACQUISITION_PART_KEY = 'ssr_ordinary_acquisition_part';
export const SSR_CHOICE_PROTECTION_PERCENT_KEY = 'ssr_choice_protection_percent';
export const DEFAULT_SSR_CHOICE_RATIO_PART = 1;
export const DEFAULT_SSR_ORDINARY_RATIO_PART = 5;
export const DEFAULT_SSR_CHOICE_PROTECTION_PERCENT = 98;
export const MIN_SSR_ACQUISITION_RATIO_PART = 1;
export const MAX_SSR_ACQUISITION_RATIO_PART = 100;
export const MIN_SSR_CHOICE_PROTECTION_PERCENT = 50;
export const MAX_SSR_CHOICE_PROTECTION_PERCENT = 100;
export const SSR_CHOICE_PROTECTION_PARAMETER_TEMPLATES = [
    {
        key: SSR_CHOICE_ACQUISITION_PART_KEY,
        name: '自选比例',
        valueKind: 'integer',
        defaultValue: String(DEFAULT_SSR_CHOICE_RATIO_PART),
        minimum: String(MIN_SSR_ACQUISITION_RATIO_PART),
        maximum: String(MAX_SSR_ACQUISITION_RATIO_PART),
        choices: [],
    },
    {
        key: SSR_ORDINARY_ACQUISITION_PART_KEY,
        name: '普通比例',
        valueKind: 'integer',
        defaultValue: String(DEFAULT_SSR_ORDINARY_RATIO_PART),
        minimum: String(MIN_SSR_ACQUISITION_RATIO_PART),
        maximum: String(MAX_SSR_ACQUISITION_RATIO_PART),
        choices: [],
    },
    {
        key: SSR_CHOICE_PROTECTION_PERCENT_KEY,
        name: '随机保护',
        valueKind: 'decimal',
        defaultValue: String(DEFAULT_SSR_CHOICE_PROTECTION_PERCENT),
        minimum: String(MIN_SSR_CHOICE_PROTECTION_PERCENT),
        maximum: String(MAX_SSR_CHOICE_PROTECTION_PERCENT),
        choices: [],
    },
];
export const WHEEL_STAGE_MODEL_VERSION = 'rank-reset-v11-ssr-probability-guard';
export const SOURCE_ATTRIBUTE_MAPPINGS = {
    'source.global.attack': ['metric.global.attack'],
    'source.global.hp': ['metric.global.hp'],
    'source.global.physical-defense': ['metric.global.physical-defense'],
    'source.global.magic-defense': ['metric.global.magic-defense'],
    'source.global.all-stats': ['metric.global.attack', 'metric.global.hp', 'metric.global.physical-defense', 'metric.global.magic-defense'],
    'source.global.fire': ['metric.global.fire'],
    'source.global.wind': ['metric.global.wind'],
    'source.global.water': ['metric.global.water'],
    'source.global.earth': ['metric.global.earth'],
    'source.global.spirit': ['metric.global.spirit'],
    'source.global.all-element': ['metric.global.fire', 'metric.global.wind', 'metric.global.water', 'metric.global.earth', 'metric.global.spirit'],
    'source.global.element-resilience': ['metric.global.fire', 'metric.global.wind', 'metric.global.water', 'metric.global.earth', 'metric.global.spirit'],
    'source.global.element-resilience-break': ['metric.global.fire', 'metric.global.wind', 'metric.global.water', 'metric.global.earth', 'metric.global.spirit'],
    'source.fate-value': ['metric.fate-value']
};
export function isPercentAllowed(sourceAttributeId) { return sourceAttributeId !== 'source.fate-value'; }
export function chineseValueKind(kind) { return kind === 'flat' ? '数值' : '%'; }
export function compactWheelTagName(name) { return name.replace(/^(火|风|水|土|精神|全)元素$/, '$1'); }
export function wheelTagNames(wheel, tags) { const byId = new Map(tags.map((tag) => [tag.id, tag])); return wheel.tagIds.map((id) => compactWheelTagName(byId.get(id)?.name ?? id)); }
export function resourceGroupForCharacter(character) { return character.rarity === 'SSR' && character.isLimited ? 'SSR_LIMITED' : character.rarity; }
function nonnegativeInteger(value) {
    return typeof value === 'number' && Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0;
}
export function backpackFragmentCount(input) {
    if (!input)
        return 0;
    return input.backpackFragmentCount === undefined
        ? nonnegativeInteger(input.fragmentCount)
        : nonnegativeInteger(input.backpackFragmentCount);
}
export function itemFragmentCount(input) {
    return nonnegativeInteger(input?.itemFragmentCount);
}
export function totalFragmentCount(input) {
    return backpackFragmentCount(input) + itemFragmentCount(input);
}
export function fragmentConstraintIgnored(input, character, legacyIgnoredGroups = []) {
    return input?.ignoreFragments === true
        || (input?.ignoreFragments === undefined
            && Boolean(character && legacyIgnoredGroups.includes(resourceGroupForCharacter(character))));
}
export function normalizeInventoryInputs(inventory, characters, legacyIgnoredGroups = []) {
    const byId = new Map(inventory.map((item) => [item.characterId, item]));
    const knownIds = new Set(characters.map((character) => character.id));
    const normalize = (item, character) => ({
        characterId: item.characterId,
        wheelCount: nonnegativeInteger(item.wheelCount),
        backpackFragmentCount: backpackFragmentCount(item),
        itemFragmentCount: itemFragmentCount(item),
        ignoreFragments: fragmentConstraintIgnored(item, character, legacyIgnoredGroups),
    });
    return [
        ...characters.map((character) => normalize(byId.get(character.id) ?? { characterId: character.id, wheelCount: 0 }, character)),
        ...inventory.filter((item) => !knownIds.has(item.characterId)).map((item) => normalize(item)),
    ];
}
function migrateSolverInventory(inventory, solver, characters) {
    return {
        inventory: normalizeInventoryInputs(inventory, characters, solver.ignoredFragmentGroups),
        solver: {
            ...solver,
            ignoredFragmentGroups: [],
            parameters: solver.parameters.filter((parameter) => parameter.key !== 'time_limit_seconds' && parameter.key !== 'threads'),
        },
    };
}
/**
 * 将旧版单一碎片字段与按品质忽略迁移为“背包 + 道具 + 单命轮忽略”。
 * 求解历史也会携带自己的迁移后快照，确保导入旧记录时语义不变。
 */
export function migrateUserPlanFragmentInventory(plan, workbench) {
    const root = migrateSolverInventory(plan.inventory, plan.solver, workbench.characters);
    return {
        ...plan,
        schemaVersion: '4.2',
        inventory: root.inventory,
        solver: root.solver,
        ...(plan.studies ? { studies: plan.studies.map((study) => {
                const migrated = migrateSolverInventory(study.inventory, study.solver, workbench.characters);
                return { ...study, inventory: migrated.inventory, solver: migrated.solver };
            }) } : {}),
        solverHistory: plan.solverHistory.map((entry) => {
            const migrated = migrateSolverInventory(entry.inventory, entry.configuration, workbench.characters);
            return { ...entry, inventory: migrated.inventory, configuration: migrated.solver };
        }),
    };
}
//# sourceMappingURL=models.js.map